import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminTabs from '../components/admin/AdminTabs';
import AdminInstallPrompt from '../components/admin/AdminInstallPrompt';
import OverviewTab from '../components/admin/OverviewTab';
import AnalyticsTab from '../components/admin/AnalyticsTab';
import GeographyTab from '../components/admin/GeographyTab';
import VideoViewsTab from '../components/admin/VideoViewsTab';
import CommentsModerationTab from '../components/admin/CommentsModerationTab';
import ArticleCommentsModerationTab from '../components/admin/ArticleCommentsModerationTab';
import TickerTab from '../components/admin/TickerTab';
import IntroQuotesTab from '../components/admin/IntroQuotesTab';
import IpBlocklistTab from '../components/admin/IpBlocklistTab';
import BannedEmailsTab from '../components/admin/BannedEmailsTab';
import BanTriggersTab from '../components/admin/BanTriggersTab';
import PushTab from '../components/admin/PushTab';
import TipWallTab from '../components/admin/TipWallTab';
import CrimeMapTab from '../components/admin/CrimeMapTab';
import ExperiencesTab from '../components/admin/ExperiencesTab';
import PrivateTab from '../components/admin/PrivateTab';
import AdsTab from '../components/admin/AdsTab';
import SubscribersTab from '../components/admin/SubscribersTab';
import EmailCampaignsTab from '../components/admin/EmailCampaignsTab';
import VideosTab from '../components/admin/VideosTab';
import UsersTab from '../components/admin/UsersTab';
import ArticlesTab from '../components/admin/ArticlesTab';
import AudioBrandingTab from '../components/admin/AudioBrandingTab';
import FoundersTab from '../components/admin/FoundersTab';
import VoicemailTab from '../components/admin/VoicemailTab';
import AdminErrorBoundary from '../components/admin/AdminErrorBoundary';
import { ADMIN_LOGIN_PATH } from '../App';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [pendingAction, setPendingAction] = useState(null);
  const navigate = useNavigate();

  // Parse deep-link query params (used by PWA shortcuts like ?tab=articles&action=new)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    const action = params.get('action');
    const validTabs = [
      'overview', 'articles', 'analytics', 'geography', 'video-views',
      'comments', 'article-comments', 'ticker', 'quotes', 'blocklist', 'banned-emails', 'triggers', 'push', 'tipwall', 'crimemap', 'ads',
      'subscribers', 'videos', 'founders', 'voicemail',
    ];
    if (tab && validTabs.includes(tab)) {
      setActiveTab(tab);
    }
    if (action) {
      setPendingAction({ tab, action });
    }
    if (tab || action) {
      // Clean the URL so refresh doesn't re-trigger the action
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
    }
  }, []);

  const [token, setToken] = useState(() => localStorage.getItem('admin_token'));
  const [username, setUsername] = useState(() => localStorage.getItem('admin_username'));
  const [restoring, setRestoring] = useState(!localStorage.getItem('admin_token'));

  const handleLogout = useCallback(async () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_username');
    // Wait for the cookie to be cleared, otherwise the login page resumes the
    // session straight back off the still-valid cookie.
    try {
      await fetch(`${API}/api/admin/logout`, { method: 'POST', credentials: 'include' });
    } catch { /* ignore */ }
    setToken(null);
    navigate(ADMIN_LOGIN_PATH);
  }, [navigate]);

  const authHeaders = { 'Authorization': `Bearer ${token}` };

  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/admin/dashboard`, { headers: authHeaders });
      if (response.status === 401) {
        handleLogout();
        return;
      }
      const data = await response.json();
      setAnalytics(data);
    } catch (err) {
      setError('Failed to load analytics');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, handleLogout]);

  const fetchSubscribers = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/admin/newsletter/subscribers`, { headers: authHeaders });
      if (response.ok) {
        const data = await response.json();
        setSubscribers(data.subscribers);
      }
    } catch (err) {
      console.error('Failed to load subscribers');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchVideos = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/videos/admin/list`, { headers: authHeaders });
      if (response.ok) {
        const data = await response.json();
        setVideos(data.videos);
      }
    } catch (err) {
      console.error('Failed to load videos');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    // Hide admin dashboard from indexing + bookmark previews
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow, noarchive, nosnippet';
    document.head.appendChild(meta);
    const prevTitle = document.title;
    document.title = '-';
    return () => {
      document.head.removeChild(meta);
      document.title = prevTitle;
    };
  }, []);

  // Restore the Bearer token from the httpOnly session cookie when
  // localStorage is empty (fresh browser, cleared storage, new tab).
  useEffect(() => {
    if (token) { setRestoring(false); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/admin/session`, { credentials: 'include' });
        if (cancelled) return;
        if (r.ok) {
          const data = await r.json();
          localStorage.setItem('admin_token', data.token);
          localStorage.setItem('admin_username', data.username);
          setToken(data.token);
          setUsername(data.username);
        }
      } catch { /* ignore */ }
      if (!cancelled) setRestoring(false);
    })();
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    if (!token) {
      if (!restoring) navigate(ADMIN_LOGIN_PATH);
      return;
    }
    fetchAnalytics();
    fetchSubscribers();
    if (activeTab === 'videos') {
      fetchVideos();
    }
  }, [token, restoring, navigate, activeTab, fetchAnalytics, fetchSubscribers, fetchVideos]);

  const handleExportCSV = async () => {
    try {
      const response = await fetch(`${API}/api/admin/newsletter/export`, { headers: authHeaders });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'newsletter_subscribers.csv';
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
    } catch (err) {
      alert('Failed to export CSV');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center" data-testid="admin-loading">
        <div className="text-white text-xl">Loading analytics...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white lg:flex" data-testid="admin-dashboard">
      <AdminTabs activeTab={activeTab} onChange={setActiveTab} onLogout={handleLogout} />

      <div className="flex-1 min-w-0 max-w-full overflow-x-hidden">
        <header className="hidden lg:block bg-zinc-900/80 backdrop-blur-xl border-b border-red-900/30 sticky top-0 z-20">
          <div className="px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-xl sm:text-2xl font-black text-white">OUTLAW MEDIA</h1>
                <p className="text-red-400 text-xs sm:text-sm font-medium">Admin Console</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="hidden sm:inline text-gray-400 text-sm">Welcome, {username}</span>
                <button
                  onClick={handleLogout}
                  data-testid="admin-logout-btn"
                  className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded text-sm transition-colors"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </header>

        <div className="px-3 sm:px-6 lg:px-8 py-4 sm:py-8 max-w-full overflow-x-hidden">
          <AdminInstallPrompt />

          {error && (
            <div className="bg-red-900/20 border border-red-500/50 text-red-400 px-4 py-3 rounded mb-6 text-sm" data-testid="admin-error">
              {error}
            </div>
          )}

        {activeTab === 'overview' && <AdminErrorBoundary tabId="overview"><OverviewTab analytics={analytics} token={token} /></AdminErrorBoundary>}
        {activeTab === 'accounts' && <AdminErrorBoundary tabId="accounts"><UsersTab token={token} /></AdminErrorBoundary>}
        {activeTab === 'articles' && (
          <AdminErrorBoundary tabId="articles">
            <ArticlesTab
              token={token}
              initialAction={pendingAction?.tab === 'articles' ? pendingAction.action : null}
              onConsumeAction={() => setPendingAction(null)}
            />
          </AdminErrorBoundary>
        )}
        {activeTab === 'analytics' && <AdminErrorBoundary tabId="analytics"><AnalyticsTab analytics={analytics} token={token} /></AdminErrorBoundary>}
        {activeTab === 'geography' && <AdminErrorBoundary tabId="geography"><GeographyTab token={token} /></AdminErrorBoundary>}
        {activeTab === 'video-views' && <AdminErrorBoundary tabId="video-views"><VideoViewsTab token={token} /></AdminErrorBoundary>}
        {activeTab === 'comments' && <AdminErrorBoundary tabId="comments"><CommentsModerationTab token={token} /></AdminErrorBoundary>}
        {activeTab === 'article-comments' && <AdminErrorBoundary tabId="article-comments"><ArticleCommentsModerationTab token={token} /></AdminErrorBoundary>}
        {activeTab === 'ticker' && <AdminErrorBoundary tabId="ticker"><TickerTab token={token} /></AdminErrorBoundary>}
        {activeTab === 'audio' && <AdminErrorBoundary tabId="audio"><AudioBrandingTab token={token} /></AdminErrorBoundary>}
        {activeTab === 'quotes' && <AdminErrorBoundary tabId="quotes"><IntroQuotesTab token={token} /></AdminErrorBoundary>}
        {activeTab === 'blocklist' && <AdminErrorBoundary tabId="blocklist"><IpBlocklistTab token={token} /></AdminErrorBoundary>}
        {activeTab === 'banned-emails' && <AdminErrorBoundary tabId="banned-emails"><BannedEmailsTab token={token} /></AdminErrorBoundary>}
        {activeTab === 'triggers' && <AdminErrorBoundary tabId="triggers"><BanTriggersTab token={token} /></AdminErrorBoundary>}
        {activeTab === 'push' && <AdminErrorBoundary tabId="push"><PushTab token={token} /></AdminErrorBoundary>}
        {activeTab === 'broadcasts' && <AdminErrorBoundary tabId="broadcasts"><EmailCampaignsTab token={token} /></AdminErrorBoundary>}
        {activeTab === 'tipwall' && <AdminErrorBoundary tabId="tipwall"><TipWallTab token={token} /></AdminErrorBoundary>}
        {activeTab === 'crimemap' && <AdminErrorBoundary tabId="crimemap"><CrimeMapTab token={token} /></AdminErrorBoundary>}
        {activeTab === 'experiences' && <AdminErrorBoundary tabId="experiences"><ExperiencesTab token={token} /></AdminErrorBoundary>}
        {activeTab === 'private' && <AdminErrorBoundary tabId="private"><PrivateTab token={token} /></AdminErrorBoundary>}
        {activeTab === 'ads' && <AdminErrorBoundary tabId="ads"><AdsTab token={token} /></AdminErrorBoundary>}
        {activeTab === 'subscribers' && (
          <AdminErrorBoundary tabId="subscribers">
            <SubscribersTab
              subscribers={subscribers}
              onExportCSV={handleExportCSV}
              onRefresh={fetchSubscribers}
              token={token}
            />
          </AdminErrorBoundary>
        )}
        {activeTab === 'videos' && (
          <AdminErrorBoundary tabId="videos">
            <VideosTab token={token} videos={videos} onRefresh={fetchVideos} />
          </AdminErrorBoundary>
        )}
        {activeTab === 'founders' && <AdminErrorBoundary tabId="founders"><FoundersTab token={token} /></AdminErrorBoundary>}
        {activeTab === 'voicemail' && <AdminErrorBoundary tabId="voicemail"><VoicemailTab token={token} /></AdminErrorBoundary>}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
